/** Shared unit icon path mapping — used by TurnGameScene and MapSelectScene. */

export const UNIT_ICON_PATHS: Record<string, string> = {
  infantry: '/units/infantry/se.png',
  'infantry-bazooka': '/units/infantry-bazooka/se.png',
  officer: '/units/officer/se.png',
  recon: '/units/recon/se.png',
  cavalry: '/units/cavalry/se.png',
  jeep: '/units/jeep/se.png',
  'light-tank': '/units/light-tank/se.png',
  'medium-tank': '/units/medium-tank/se.png',
  'heavy-tank': '/units/heavy-tank/se.png',
  apc: '/units/apc/se.png',
  'aa-flakpanzer': '/units/aa-flakpanzer/se.png',
  'tank-destroyer': '/units/tank-destroyer/se.png',
  'long-range-artillery': '/units/long-range-artillery/se.png',
  'anti-tank-artillery': '/units/anti-tank-artillery/se.png',
  'supply-truck': '/units/supply-truck/se.png',
  medic: '/units/medic/se.png',
  'plane-fighter': '/units/plane-fighter/se.png',
  'plane-bomber': '/units/plane-bomber/se.png',
  'plane-transport': '/units/plane-transport/se.png',
  'ship-battleship': '/units/ship-battleship/se.png',
  'ship-submarine': '/units/ship-submarine/se.png',
  'ship-transport': '/units/ship-transport/se.png',
  'ship-aircraft-carrier': '/units/ship-aircraft-carrier/se.png',
  // Faction-unique units (dedicated icons where available, fallback to base type)
  'polska-ulani': '/units/polska-ulani/e.png',
  'polska-7tp': '/units/polska-7tp/se.png',
  'zsrr-t34': '/units/zsrr-t34/se.png',
  'zsrr-katiusza': '/units/zsrr-katiusza/se.png',
  'iii-rzesza-tiger': '/units/iii-rzesza-tiger/se.png',
  'iii-rzesza-stuka': '/units/iii-rzesza-stuka/se.png',
  'usa-sherman': '/units/m4sherman-map-icon.png',
  'usa-lcvp-higgins': '/units/ship-transport/se.png',
  'wlochy-l3-33': '/units/light-tank/se.png',
  'wlochy-sm79-sparviero': '/units/plane-fighter/se.png',
  'japonia-chi-ha': '/units/medium-tank/se.png',
  'japonia-a6m-zero': '/units/plane-fighter/se.png',
  'francja-renault-r35': '/units/light-tank/se.png',
  'francja-ms406': '/units/plane-fighter/se.png',
  'jugoslawia-partyzanci': '/units/infantry/se.png',
  'jugoslawia-ba64': '/units/recon/se.png',
  'wielka-brytania-komandosi': '/units/infantry/se.png',
  'wielka-brytania-churchill': '/units/churchill-map-icon.png',
  // Heroes — pixel-art directional sprites (6 facings) under /units/scientists/<key>/.
  // The icon path is the `se` frame; the full directional set is wired in
  // TurnGameScene's DIRECTIONAL_UNITS. unitInfoIcon() shows the bust portrait instead.
  'hero-polska-a': '/units/scientists/maria-sklodowska-curie/se.png',
  'hero-polska-b': '/units/scientists/jan-czochralski/se.png',
  'hero-iii-rzesza-a': '/units/scientists/fritz-haber/se.png',
  'hero-iii-rzesza-b': '/units/scientists/karl-landsteiner/se.png',
  'hero-francja-a': '/units/scientists/jean-perrin/se.png',
  'hero-francja-b': '/units/scientists/alexis-carrel/se.png',
  'hero-zsrr-a': '/units/scientists/konstantin-tsiolkovsky/se.png',
  'hero-zsrr-b': '/units/scientists/ivan-pavlov/se.png',
  'hero-usa-a': '/units/scientists/thomas-edison/se.png',
  'hero-usa-b': '/units/scientists/robert-goddard/se.png',
  'hero-wlochy-a': '/units/scientists/guglielmo-marconi/se.png',
  'hero-wlochy-b': '/units/scientists/vito-volterra/se.png',
  'hero-japonia-a': '/units/scientists/hideyo-noguchi/se.png',
  'hero-japonia-b': '/units/scientists/shibasaburo-kitasato/se.png',
  'hero-jugoslawia-a': '/units/scientists/nikola-tesla/se.png',
  'hero-jugoslawia-b': '/units/scientists/mihajlo-pupin/se.png',
  'hero-wielka-brytania-a': '/units/scientists/ernest-rutherford/se.png',
  'hero-wielka-brytania-b': '/units/scientists/joseph-john-thomson/se.png',
};

/** Get the icon path for a unit type, or undefined if not mapped. */
export function unitIconPath(type: string): string | undefined {
  return UNIT_ICON_PATHS[type];
}

/** Hero unit type → scientist key (filename stem shared by portrait + sprite set). */
export const HERO_KEYS: Record<string, string> = {
  'hero-polska-a': 'maria-sklodowska-curie',
  'hero-polska-b': 'jan-czochralski',
  'hero-iii-rzesza-a': 'fritz-haber',
  'hero-iii-rzesza-b': 'karl-landsteiner',
  'hero-francja-a': 'jean-perrin',
  'hero-francja-b': 'alexis-carrel',
  'hero-zsrr-a': 'konstantin-tsiolkovsky',
  'hero-zsrr-b': 'ivan-pavlov',
  'hero-usa-a': 'thomas-edison',
  'hero-usa-b': 'robert-goddard',
  'hero-wlochy-a': 'guglielmo-marconi',
  'hero-wlochy-b': 'vito-volterra',
  'hero-japonia-a': 'hideyo-noguchi',
  'hero-japonia-b': 'shibasaburo-kitasato',
  'hero-jugoslawia-a': 'nikola-tesla',
  'hero-jugoslawia-b': 'mihajlo-pupin',
  'hero-wielka-brytania-a': 'ernest-rutherford',
  'hero-wielka-brytania-b': 'joseph-john-thomson',
};

/** Bust portrait path for a hero unit type (shown in info/recruit panels), or undefined. */
export function heroPortrait(type: string): string | undefined {
  const key = HERO_KEYS[type];
  return key ? `/portraits/${key}.png` : undefined;
}
